import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Info } from 'lucide-react';
import AppHeader from './AppHeader';

type PasswordResetOutcome = 'sent' | 'not_found' | 'google_only';

type FeedbackStatus = 'idle' | 'success' | 'error' | 'info';

const requestPasswordResetFn = httpsCallable<
  { email: string },
  { success: boolean; outcome: PasswordResetOutcome; message: string }
>(functions, 'requestPasswordReset');

const ForgotPassword: React.FC = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<FeedbackStatus>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const prefill = (location.state as { email?: string } | null)?.email;
    if (prefill) {
      setEmail(prefill);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setStatus('error');
      setMessage('Por favor, informe seu e-mail');
      return;
    }

    setIsLoading(true);
    setStatus('idle');
    setMessage('');

    try {
      const result = await requestPasswordResetFn({ email: email.trim() });
      const data = result.data;
      const text = data?.message || 'Não foi possível processar sua solicitação.';

      if (data?.outcome === 'sent' && data.success) {
        setStatus('success');
        setMessage(text);
        setEmail('');
        return;
      }

      if (data?.outcome === 'google_only') {
        setStatus('info');
        setMessage(text);
        return;
      }

      if (data?.outcome === 'not_found') {
        setStatus('error');
        setMessage(text);
        return;
      }

      setStatus(data?.success ? 'success' : 'error');
      setMessage(text);
      if (data?.success) {
        setEmail('');
      }
    } catch (error: unknown) {
      console.error('Erro ao enviar e-mail de recuperação:', error);
      setStatus('error');

      const code = String((error as { code?: string })?.code || '');
      const details = String((error as { details?: unknown })?.details || '');
      const raw = String((error as { message?: string })?.message || '');
      const combined = `${code} ${raw} ${details}`.toLowerCase();

      if (code === 'functions/invalid-argument') {
        setMessage('E-mail inválido');
      } else if (code === 'functions/resource-exhausted') {
        setMessage('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
      } else if (code === 'functions/unavailable') {
        setMessage(
          raw && !/^internal$/i.test(raw.trim())
            ? raw
            : 'Não foi possível enviar o e-mail agora. Tente novamente em alguns minutos.'
        );
      } else if (
        code === 'functions/failed-precondition' ||
        combined.includes('google')
      ) {
        setStatus('info');
        setMessage(
          raw && !/^internal$/i.test(raw.trim())
            ? raw
            : 'Esta conta foi criada com Google. Para entrar, use o botão "Entrar com Google" na tela de login — não há senha para recuperar aqui.'
        );
      } else if (!raw || code === 'functions/internal' || /^internal$/i.test(raw.trim())) {
        setMessage('Não foi possível enviar o e-mail agora. Se você entra com Google, use o botão Google na tela de login.');
      } else {
        setMessage(raw);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#4A7C75] to-[#2D5A54] flex flex-col">
      <AppHeader />

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4A7C75] bg-opacity-10 rounded-full mb-4">
                <Mail className="w-8 h-8 text-[#4A7C75]" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Recuperar Senha
              </h1>
              <p className="text-gray-600">
                Para contas criadas com e-mail e senha. Quem entra com Google não precisa redefinir senha aqui.
              </p>
            </div>

            {status === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{message}</p>
              </div>
            )}

            {status === 'info' && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">{message}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-2">
                  E-mail
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                    <Mail className="w-5 h-5" />
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-3 bg-[#E8E8E8] rounded-full border-2 border-transparent focus:border-[#4A7C75] focus:bg-white outline-none transition text-gray-700 disabled:opacity-50"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-[#4A7C75] text-white rounded-full font-medium hover:bg-[#3A6C65] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Enviando...' : 'Enviar link de recuperação'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-[#4A7C75] hover:text-[#3A6C65] font-medium transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para o login
              </Link>
            </div>
          </div>

          <div className="mt-6 text-center text-white text-sm space-y-1">
            <p>O link de recuperação expira em 1 hora</p>
            <p className="text-white/80">Entrou com Google? Use o botão Google na tela de login.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
